<template>
  <div class="min-h-screen bg-black text-white font-sans selection:bg-purple-500 selection:text-white overflow-x-hidden">
    <div class="fixed inset-0 z-0 pointer-events-none">
      <div class="absolute top-[-20%] left-[-10%] h-[50%] w-[50%] rounded-full bg-purple-900/20 blur-[128px]" />
      <div class="absolute bottom-[-20%] right-[-10%] h-[50%] w-[50%] rounded-full bg-pink-900/20 blur-[128px]" />
      <div class="absolute inset-0 bg-[url('/grid.svg')] opacity-[0.04]" />
    </div>

    <main class="relative z-10 mx-auto max-w-6xl px-6 py-16 md:py-24">
      <NuxtLink
        to="/"
        class="mb-10 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-white"
      >
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
        Back to home
      </NuxtLink>

      <section class="mb-12 grid gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)] lg:items-end">
        <div>
          <div class="mb-4 inline-flex rounded-full border border-purple-500/30 bg-purple-900/20 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-purple-300">
            From The Studio
          </div>
          <h1 class="max-w-3xl text-5xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-white via-gray-200 to-gray-500 md:text-7xl">
            Release Notes
          </h1>
          <p class="mt-5 max-w-2xl text-lg leading-relaxed text-gray-400">
            This is where we share the updates we have shipped for Vibe Games, from new features to gameplay polish and live improvements.
          </p>
        </div>

        <div v-if="latestShippedRelease" class="rounded-3xl border border-gray-800 bg-gray-900/60 p-6 backdrop-blur-sm">
          <div class="text-xs font-bold uppercase tracking-[0.16em] text-emerald-300">Latest release</div>
          <div class="mt-3 text-2xl font-black text-white">{{ latestShippedRelease.id }}</div>
          <div class="mt-1 text-sm uppercase tracking-[0.16em] text-gray-500">{{ formatReleaseDate(latestShippedRelease.releaseDate) }}</div>
          <div class="mt-4 text-xl font-bold text-white">{{ latestShippedRelease.theme }}</div>
          <p class="mt-3 text-sm leading-relaxed text-gray-400">{{ latestShippedRelease.summary }}</p>
          <ul class="mt-5 space-y-2 text-sm text-gray-200">
            <li v-for="highlight in latestShippedRelease.highlights" :key="highlight" class="flex items-start gap-2">
              <span class="mt-0.5 text-emerald-300">+</span>
              <span>{{ highlight }}</span>
            </li>
          </ul>
        </div>
      </section>

      <section v-if="shippedReleases.length > 0">
        <div class="mb-6 flex items-center justify-between gap-4">
          <div>
            <h2 class="text-2xl font-black text-white md:text-3xl">More shipped updates</h2>
            <p class="mt-2 text-sm text-gray-500">A running look at the work we have already pushed live.</p>
          </div>
          <div class="rounded-full border border-gray-800 bg-gray-900/60 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-gray-400">
            {{ shippedReleases.length }} shipped
          </div>
        </div>

        <div class="grid gap-5 lg:grid-cols-2">
          <article
            v-for="release in shippedReleases"
            :key="release.id"
            class="rounded-3xl border border-gray-800 bg-gray-900/60 p-6 backdrop-blur-sm"
          >
            <div class="flex flex-wrap items-center gap-3">
              <span class="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-[0.7rem] font-bold uppercase tracking-[0.16em] text-emerald-300">
                Shipped
              </span>
              <span class="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">{{ formatReleaseDate(release.releaseDate) }}</span>
            </div>
            <div class="mt-4 text-sm font-bold uppercase tracking-[0.16em] text-purple-300">{{ release.id }}</div>
            <h3 class="mt-2 text-2xl font-black text-white">{{ release.theme }}</h3>
            <p class="mt-4 text-sm leading-relaxed text-gray-400">{{ release.summary }}</p>
            <ul class="mt-5 space-y-2 text-sm text-gray-200">
              <li v-for="highlight in release.highlights" :key="highlight" class="flex items-start gap-2">
                <span class="mt-0.5 text-emerald-300">+</span>
                <span>{{ highlight }}</span>
              </li>
            </ul>
          </article>
        </div>
      </section>
    </main>

    <footer class="relative z-10 border-t border-gray-900 py-6 text-center text-xs text-gray-600">
      © {{ new Date().getFullYear() }} Vibe Labs. All rights reserved.
    </footer>
  </div>
</template>

<script setup lang="ts">
import { getLatestShippedFeatureRelease, getShippedFeatureReleases } from '~/utils/featureReleaseCatalog'

const config = useRuntimeConfig()
const latestShippedRelease = getLatestShippedFeatureRelease()
const shippedReleases = getShippedFeatureReleases()
const releasesTitle = 'Feature Releases'
const releasesDescription = 'Track shipped updates and upcoming feature drops for Vibe Games.'

useHead({
  link: [
    { rel: 'canonical', href: `${config.public.siteUrl}/releases` }
  ]
})

useSeoMeta({
  title: releasesTitle,
  description: releasesDescription,
  ogType: 'website',
  ogTitle: releasesTitle,
  ogDescription: releasesDescription,
  ogUrl: `${config.public.siteUrl}/releases`,
})

const formatReleaseDate = (releaseDate: string) => {
    return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    }).format(new Date(releaseDate))
}
</script>
